import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import { BadgeCheck, Bot, Building2, GitBranch, KeyRound, Languages, LayoutGrid, Moon, Plus, Search, Settings, Smartphone, Tag, Users } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type * as S from "@releo/shared";
import { setLanguage } from "@/i18n";
import { useApps, useRecentBuilds, useTags } from "@/lib/queries";
import { isInstanceAdmin, isOrgAdmin } from "@/lib/roles";
import { timeAgo } from "@/lib/utils";
import { PlatformIcon, StatusBadge } from "./common";
import { TagBadge, TagList } from "./tags";
import { Spinner } from "./ui";

/** Opens the palette with ⌘K / Ctrl+K. */
export function useCommandPaletteShortcut(setOpen: (update: (open: boolean) => boolean) => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const SHORTCUT_LABEL = isMac ? "⌘K" : "Ctrl K";

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Every word of the query must appear in one of the fields. */
function matches(query: string, fields: (string | null | undefined)[]): boolean {
  const haystack = fields.filter(Boolean).join(" ").toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

function Item({ value, onSelect, icon, children, hint }: { value: string; onSelect: () => void; icon?: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2 text-sm data-[selected=true]:bg-muted"
    >
      {icon && <span className="flex w-4 shrink-0 justify-center text-fg-muted">{icon}</span>}
      <span className="flex min-w-0 flex-1 items-center gap-2">{children}</span>
      {hint && <span className="shrink-0 text-xs text-fg-muted">{hint}</span>}
    </Command.Item>
  );
}

const groupClass =
  "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-fg-muted";

export function CommandPalette({
  open,
  onOpenChange,
  me,
  dark,
  toggleTheme,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  me: S.Me;
  dark: boolean;
  toggleTheme: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const q = query.trim();
  const debounced = useDebounced(q, 200);
  const apps = useApps();
  const tags = useTags();
  const builds = useRecentBuilds(q ? 8 : 5, debounced, open);
  const isAdmin = isOrgAdmin(me);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const appNames = useMemo(() => new Map((apps.data ?? []).map((a) => [a.id, a.name])), [apps.data]);
  const appResults = (apps.data ?? []).filter((a) => matches(q, [a.name, a.iosBundleId, a.androidPackage, ...a.tags.map((tag) => tag.name)])).slice(0, q ? 8 : 6);
  const tagResults = q ? (tags.data ?? []).filter((tag) => matches(q, [tag.name])).slice(0, 6) : [];
  const buildResults = builds.isPlaceholderData || debounced !== q ? [] : (builds.data ?? []);

  const actions: { id: string; label: string; icon: ReactNode; run: () => void; keywords?: string }[] = [
    { id: "apps", label: t("search.goApps"), icon: <LayoutGrid className="h-4 w-4" />, run: () => navigate({ to: "/apps" }) },
    ...(isAdmin ? [{ id: "new-app", label: t("apps.new"), icon: <Plus className="h-4 w-4" />, run: () => navigate({ to: "/apps/new" }) }] : []),
    { id: "settings", label: t("nav.settings"), icon: <Settings className="h-4 w-4" />, run: () => navigate({ to: "/settings" }), keywords: "api keys" },
    ...(isAdmin
      ? [
          { id: "organization", label: t("nav.organization"), icon: <Building2 className="h-4 w-4" />, run: () => navigate({ to: "/organization" }), keywords: "members" },
          { id: "credentials", label: t("nav.credentials"), icon: <KeyRound className="h-4 w-4" />, run: () => navigate({ to: "/admin/credentials" }), keywords: "apple google" },
          { id: "llm", label: t("nav.llm"), icon: <Bot className="h-4 w-4" />, run: () => navigate({ to: "/admin/llm" }), keywords: "ai" },
          { id: "git", label: t("nav.git"), icon: <GitBranch className="h-4 w-4" />, run: () => navigate({ to: "/admin/git" }), keywords: "github gitlab azure" },
          { id: "tags", label: t("nav.tags"), icon: <Tag className="h-4 w-4" />, run: () => navigate({ to: "/admin/tags" }) },
          { id: "devices", label: t("nav.devices"), icon: <Smartphone className="h-4 w-4" />, run: () => navigate({ to: "/admin/devices" }), keywords: "udid iphone" },
        ]
      : []),
    ...(isInstanceAdmin(me)
      ? [
          { id: "users", label: t("nav.users"), icon: <Users className="h-4 w-4" />, run: () => navigate({ to: "/admin/users" }) },
          { id: "license", label: t("nav.license"), icon: <BadgeCheck className="h-4 w-4" />, run: () => navigate({ to: "/admin/license" }), keywords: "enterprise license license" },
        ]
      : []),
    { id: "theme", label: t(dark ? "search.lightTheme" : "search.darkTheme"), icon: <Moon className="h-4 w-4" />, run: toggleTheme, keywords: "theme dark light" },
    {
      id: "language",
      label: t("search.language"),
      icon: <Languages className="h-4 w-4" />,
      run: () => setLanguage(i18n.language === "fr" ? "en" : "fr"),
      keywords: "language langue english français",
    },
  ];
  const actionResults = actions.filter((a) => matches(q, [a.label, a.keywords]));

  const run = (fn: () => unknown) => {
    onOpenChange(false);
    void fn();
  };
  const searching = !!q && (debounced !== q || builds.isFetching);
  const empty = !appResults.length && !tagResults.length && !buildResults.length && !actionResults.length;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[1px]" />
        <DialogPrimitive.Content className="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-lg border bg-panel shadow-2xl">
          <DialogPrimitive.Title className="sr-only">{t("search.title")}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{t("search.placeholder")}</DialogPrimitive.Description>
          <Command shouldFilter={false} loop label={t("search.title")}>
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="h-4 w-4 shrink-0 text-fg-muted" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder={t("search.placeholder")}
                className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-fg-muted/70"
              />
              {searching && <Spinner />}
            </div>
            <Command.List className="max-h-[min(60vh,420px)] overflow-y-auto p-2">
              {empty && !searching && <div className="px-3 py-8 text-center text-sm text-fg-muted">{t("search.noResults")}</div>}
              {appResults.length > 0 && (
                <Command.Group heading={t("search.apps")} className={groupClass}>
                  {appResults.map((app) => (
                    <Item
                      key={app.id}
                      value={`app:${app.id}`}
                      onSelect={() => run(() => navigate({ to: "/apps/$appId", params: { appId: app.id } }))}
                      icon={app.iosBundleId ? <PlatformIcon platform="ios" /> : <PlatformIcon platform="android" />}
                      hint={app.iosBundleId ?? app.androidPackage}
                    >
                      <span className="truncate font-medium">{app.name}</span>
                      <TagList tags={app.tags} className="hidden flex-nowrap overflow-hidden sm:flex" />
                    </Item>
                  ))}
                </Command.Group>
              )}
              {tagResults.length > 0 && (
                <Command.Group heading={t("search.tags")} className={groupClass}>
                  {tagResults.map((tag) => (
                    <Item
                      key={tag.id}
                      value={`tag:${tag.id}`}
                      onSelect={() => run(() => navigate({ to: "/apps", search: { tag: tag.id } }))}
                      icon={<Tag className="h-4 w-4" />}
                      hint={t("search.appsWithTag", { count: (apps.data ?? []).filter((a) => a.tags.some((x) => x.id === tag.id)).length })}
                    >
                      <TagBadge tag={tag} />
                    </Item>
                  ))}
                </Command.Group>
              )}
              {buildResults.length > 0 && (
                <Command.Group heading={q ? t("search.builds") : t("apps.recentBuilds")} className={groupClass}>
                  {buildResults.map((b) => (
                    <Item
                      key={b.id}
                      value={`build:${b.id}`}
                      onSelect={() => run(() => navigate({ to: "/apps/$appId/builds/$buildId", params: { appId: b.appId, buildId: b.id } }))}
                      icon={<PlatformIcon platform={b.platform} />}
                      hint={timeAgo(b.createdAt, i18n.language)}
                    >
                      <span className="truncate">
                        <span className="font-medium">{appNames.get(b.appId) ?? "—"}</span> {b.version} <span className="text-fg-muted">({b.buildNumber})</span>
                      </span>
                      <StatusBadge status={b.status} />
                    </Item>
                  ))}
                </Command.Group>
              )}
              {actionResults.length > 0 && (
                <Command.Group heading={t("search.actions")} className={groupClass}>
                  {actionResults.map((a) => (
                    <Item key={a.id} value={`action:${a.id}`} onSelect={() => run(a.run)} icon={a.icon}>
                      {a.label}
                    </Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
